/**
 * Critically-damped spring camera follow (SDD §4.6).
 * The camera target is the player's torso position; the camera position is
 * applied as a translation on the world container.
 */

import { Container } from 'pixi.js';

const SETTLE_SECONDS = 0.18;

export class Camera {
  private cx = 0;
  private cy = 0;
  private vx = 0;
  private vy = 0;
  private viewportW = 1;
  private viewportH = 1;
  /** Current shake amplitude in world pixels (decays over time). */
  private shakeAmp = 0;

  constructor(private readonly worldContainer: Container) {}

  setViewport(w: number, h: number): void {
    this.viewportW = w;
    this.viewportH = h;
  }

  snapTo(x: number, y: number): void {
    this.cx = x;
    this.cy = y;
    this.vx = 0;
    this.vy = 0;
  }

  /** Trigger a screen shake. `amp` in pixels — accumulates with current. */
  shake(amp: number): void {
    if (amp > this.shakeAmp) this.shakeAmp = Math.min(40, amp);
  }

  /**
   * Step the camera toward target with a critically-damped spring.
   * dt in seconds.
   */
  update(targetX: number, targetY: number, dt: number, bounds: { minX: number; maxX: number; maxY: number }): void {
    // Look-ahead = small fraction of horizontal velocity, capped.
    const omega = 2 / SETTLE_SECONDS;
    const dx = targetX - this.cx;
    const dy = targetY - this.cy;
    const ax = (dx * omega - this.vx) * 2 * omega;
    const ay = (dy * omega - this.vy) * 2 * omega;
    this.vx += ax * dt;
    this.vy += ay * dt;
    this.cx += this.vx * dt;
    this.cy += this.vy * dt;

    // Clamp to world bounds. Two cases per axis:
    //   - World larger than viewport: keep the camera window inside bounds.
    //   - World smaller than viewport (e.g. portrait phone where the screen
    //     is taller than the level): center the world on the viewport so the
    //     player can't drift off-screen just because the level is short.
    const halfW = this.viewportW / 2;
    const halfH = this.viewportH / 2;
    const worldW = bounds.maxX - bounds.minX;
    if (worldW <= this.viewportW) {
      this.cx = bounds.minX + worldW / 2;
    } else {
      if (this.cx < bounds.minX + halfW) this.cx = bounds.minX + halfW;
      if (this.cx > bounds.maxX - halfW) this.cx = bounds.maxX - halfW;
    }
    if (bounds.maxY <= this.viewportH) {
      this.cy = bounds.maxY / 2;
    } else {
      if (this.cy > bounds.maxY - halfH) this.cy = bounds.maxY - halfH;
      if (this.cy < halfH) this.cy = halfH;
    }

    this.worldContainer.x = Math.round(halfW - this.cx);
    this.worldContainer.y = Math.round(halfH - this.cy);

    if (this.shakeAmp > 0.05) {
      const a = this.shakeAmp;
      this.worldContainer.x += Math.round((Math.random() * 2 - 1) * a);
      this.worldContainer.y += Math.round((Math.random() * 2 - 1) * a);
      // Exponential decay (~0.85 per 16ms frame at 60Hz).
      this.shakeAmp *= Math.pow(0.0001, dt);
    } else {
      this.shakeAmp = 0;
    }
  }
}
